import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../config/api'
import { Button, Alert } from '../components/ui'
import PasswordlessSignIn from '../components/PasswordlessSignIn'

const QUICK_LOGIN_USERS = [
  { email: 'chip@test.com', password: 'testing123', label: 'Chip (Owner)', role: 'OWNER' },
  { email: 'admin2@test.com', password: 'testing123', label: 'Sarah (Admin)', role: 'ADMIN' },
  { email: 'maria.santos@example.com', password: 'testing123', label: 'Maria (Respondent)', role: 'RESPONDENT' },
]

function isLocalhost() {
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1'
}

const SignInPage: React.FC = () => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [searchParams] = useSearchParams()
  const { login, loginWithTokens } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const verified = searchParams.get('verified') === 'true'
  const redirectTo = searchParams.get('redirect')

  // Localhost-only dev shortcut: seed users still have passwords.
  const handleQuickLogin = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      setError('')
      await login(email, password)
      navigate(redirectTo || '/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.detail || intl.formatMessage({ id: 'auth.signin.failed' }))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleLogin = () => {
    const returnTo = encodeURIComponent(window.location.origin)
    window.location.href = `${API_ENDPOINTS.auth.google}?return_to=${returnTo}`
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            <FormattedMessage id="auth.signin.title" />
          </h2>
          <p className="mt-2 text-center text-sm text-foreground-muted">
            <FormattedMessage id="auth.signin.subtitle" />
          </p>
        </div>

        {verified && (
          <Alert variant="success"><FormattedMessage id="auth.signin.verified" /></Alert>
        )}

        {isLocalhost() && (
          <div className="bg-surface border border-border rounded-xl p-4">
            <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-3"><FormattedMessage id="auth.signin.quickLogin" /></p>
            <div className="grid grid-cols-1 gap-2">
              {QUICK_LOGIN_USERS.map(u => (
                <button
                  key={u.email}
                  onClick={() => handleQuickLogin(u.email, u.password)}
                  disabled={isLoading}
                  className="flex items-center justify-between px-3 py-2 text-sm rounded-lg border border-border-light hover:bg-background transition-colors text-left disabled:opacity-50"
                >
                  <span className="font-medium text-foreground">{u.label}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{u.role}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <Button variant="outline" fullWidth onClick={handleGoogleLogin} className="gap-3">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <FormattedMessage id="auth.google" />
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-background text-foreground-muted"><FormattedMessage id="auth.orContinueWithEmail" /></span>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <PasswordlessSignIn
          onSuccess={async (data) => {
            await loginWithTokens(data.access_token, data.session_token, data.user.email)
            navigate(redirectTo || '/dashboard')
          }}
        />

        <p className="text-center text-sm text-foreground-muted">
          <FormattedMessage id="auth.signin.newHere" />
        </p>
      </div>
    </div>
  )
}

export default SignInPage
